import React from 'react';
import PropTypes from 'prop-types';
import fr from 'facet-rest';
import AuthenticationFactorList
  from 'Views/Page/Content/Templates/SettingsTemplate/Content/AuthenticationFactorTemplate/AuthenticationFactorsCard/AuthenticationFactorListPicker/AuthenticationFactorList';
import useBoneBackgroundColor from 'Hooks/useBoneBackgroundColor';
import {
  FDBackButton, FDBodyText, FDContentContainer, FDHeadlineText, FDPrimaryActionButton, FDStack, FDTitleText,
} from '@facet-wealth/design';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import Footer from 'Components/ClientApp/Desktop/Footer';
import useMobileLayout from 'Hooks/useMobileLayout';
import { buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import { buildFacetDesignOverride, overrideReadOnly } from 'Packages/FacetRest/FacetDesignOverride';
import { MFAAlert } from 'Components/ComponentLibrary/Molecules/MFAAlert/MFAAlert';
import MetaControl from 'Components/UI/Meta/MetaControl';
import useUserContext from 'Context/UserContext';

const useStyles = buildUseStyles('SecurityProfileSubpage', () => ({
  // The Footer by default comes with some styles that apply absolute positioning which causes it to overlap the content
  // when the content is taller than the viewport so we need to override those here.
  footer: {
    position: 'relative',
    transform: 'initial',
  },
}));

const metaOverrideSet = buildFacetDesignOverride()
  .nameOverride('title', {
    _component: () => FDHeadlineText,
    _customProps: {
      color: 'textSecondary',
    },
  }, overrideReadOnly)
  .nameOverride('subtitle', {
    _component: () => FDBodyText,
  }, overrideReadOnly)
  .nameOverride('alert', {
    _component: () => MFAAlert,
  }, overrideReadOnly);

const SecurityProfileSubpage = ({
  resource,
}) => {
  const classes = useStyles();
  const isMobile = useMobileLayout();
  const { loadPageContentLink } = usePageContentContext();
  const { userResource } = useUserContext();

  useBoneBackgroundColor();

  return (
    <>
      <FDContentContainer maxWidth={'xl'}>
        <FDStack direction={'column'} width={'100%'} spacing={3} paddingY={3}>
          {resource.withRelationship(fr.relationships.householdMembershipPage.name,
            (rel) => (
              <FDStack spacing={2} alignItems={'center'} justifyContent={'flex-start'} width={'100%'}>
                <FDBackButton onClick={() => loadPageContentLink(rel.link('related'))} test={'profile-page-back'} />
                <FDTitleText size={'lg'} color={'textSecondary'} value={rel.meta('prompt')} />
              </FDStack>),
            () => null)}
          <MetaControl resource={resource} metaOverrideSet={metaOverrideSet} attrName={'title'} />
          <MetaControl resource={resource} metaOverrideSet={metaOverrideSet} attrName={'subtitle'} />
          <AuthenticationFactorList />
          <MetaControl resource={resource} metaOverrideSet={metaOverrideSet} attrName={'alert'} />
        </FDStack>
        <FDStack spacing={2} padding={2} justifyContent={'center'}>
          {userResource.withRelationship(fr.relationships.testProtectedResourceRiskLevelHigh.name, (rel) => {
            return (
              <FDPrimaryActionButton
                label={'Test high risk endpoint'}
                onClick={() => {
                  loadPageContentLink(rel.link('related'));
                }}
              />
            );
          }, () => null)}
          {userResource.withRelationship(fr.relationships.testProtectedResourceRiskLevelMedium.name, (rel) => {
            return (
              <FDPrimaryActionButton
                label={'Test medium risk endpoint'}
                onClick={() => {
                  loadPageContentLink(rel.link('related'));
                }}
              />
            );
          }, () => null)}
          {userResource.withRelationship(fr.relationships.testProtectedResourceRiskLevelLow.name, (rel) => {
            return (
              <FDPrimaryActionButton
                label={'Test low risk endpoint'}
                onClick={() => {
                  loadPageContentLink(rel.link('related'));
                }}
              />
            );
          }, () => null)}
        </FDStack>
      </FDContentContainer>
      {!isMobile && <Footer className={classes.footer} />}
    </>
  );
};

SecurityProfileSubpage.propTypes = {
  resource: PropTypes.instanceOf(fr.Resource).isRequired,
};

export default SecurityProfileSubpage;
